/**
 * The header line a transcript's speaker turn is written with — one format, written and read here.
 *
 * A transcript is stored as ordinary normalized blocks, and the speaker and the timecode of a turn
 * live in the block's own text: `Speaker 1 · 00:12:03–00:12:41` on the first line, the words below
 * it. That is how transcripts are written, it survives search and evidence excerpts with no new
 * plumbing, and the block vocabulary has no other field that could carry the two facts without
 * meaning something else to a reader.
 *
 * Because it is text, two sides have to agree on it exactly: the ingestion that builds a block and
 * the reader that renders a turn back out of one. A second, hand-written rule on the reading side
 * would be a format that can drift on either half while every stored transcript keeps the old
 * spelling. So the format is one body — `formatTranscriptTurnHeader` writes it,
 * `parseTranscriptTurnHeader` reads it, and the two are pinned against each other by round-trip
 * tests.
 *
 * **Reading is strict**, and deliberately: a turn that was split by the render bound keeps its
 * header only on the first fragment, so every continuation block's first line is ordinary speech.
 * A parser that guessed would turn a sentence starting "Speaker of the house…" into a turn header
 * and lose the words. Only the exact shapes this module writes parse; everything else is `null`,
 * which is a reader's answer of "these are words, not chrome".
 */
/** The middle dot, spaced, between a speaker label and a timecode. */
export const TRANSCRIPT_TURN_HEADER_SEPARATOR = " · ";
/** The en dash between the two ends of a timecode range. */
export const TRANSCRIPT_TIMECODE_RANGE_SEPARATOR = "–";
/** What a diarized speaker number is called wherever a person sees it. */
export function transcriptSpeakerLabel(speaker) {
    return `Speaker ${speaker}`;
}
/** Longest a given name may be, after normalization. */
export const TRANSCRIPT_SPEAKER_NAME_MAX_LENGTH = 60;
/** Most speakers one recording may carry a given name for. */
export const TRANSCRIPT_SPEAKER_NAME_MAX_ENTRIES = 50;
/**
 * A submitted name as it is stored: NFKC, trimmed, whitespace collapsed, header separator removed.
 *
 * The separator is the one character a name may not carry: a header is `<label> · <timecode>`, so
 * a name holding it would read back as two fields. An empty result *clears* the name.
 */
export function normalizeTranscriptSpeakerName(value) {
    return value
        .normalize("NFKC")
        .replaceAll(TRANSCRIPT_TURN_HEADER_SEPARATOR.trim(), "")
        .replace(/\s+/g, " ")
        .trim();
}
/**
 * A speaker map a file supplied, cut to the bounds a person's rename is held to.
 *
 * A `<v …>` tag and a Teams export name their voices for us, and nothing about a file constrains
 * what it writes there: a caption track can carry hundreds of distinct voices and a paragraph's
 * worth of text where a name belongs. The same 50 entries of at most 60 normalized characters
 * `documentTranscript.renameSpeaker` enforces therefore apply to a map read out of bytes — the
 * difference being that a person is told their name is too long, while a file's is simply cut,
 * because the alternative is refusing the transcript over a label.
 *
 * A name that normalizes to nothing yields no entry, and a speaker past the cap keeps its number
 * and no name — so an unbounded file still reads as a transcript, with `Speaker 51` in the headers
 * its 51st voice speaks. The lowest numbers win, which is first appearance.
 */
export function boundSpeakerNames(names) {
    const bounded = {};
    let kept = 0;
    for (const key of Object.keys(names).sort((left, right) => Number(left) - Number(right))) {
        if (kept >= TRANSCRIPT_SPEAKER_NAME_MAX_ENTRIES)
            break;
        const name = normalizeTranscriptSpeakerName(names[key] ?? "")
            .slice(0, TRANSCRIPT_SPEAKER_NAME_MAX_LENGTH)
            .trimEnd();
        if (name.length === 0)
            continue;
        bounded[key] = name;
        kept += 1;
    }
    return bounded;
}
/**
 * What one speaker is called: the name this project gave them, or `Speaker N`.
 *
 * The single body every surface reads a label through — turn headers, the legend, the timeline,
 * the model-facing projection — so a rename lands everywhere at once or nowhere.
 */
export function transcriptSpeakerDisplayLabel(speaker, names) {
    const given = names?.[String(speaker)];
    return given !== undefined && given.length > 0 ? given : transcriptSpeakerLabel(speaker);
}
/**
 * `HH:MM:SS`, always — never a shorter form for a shorter recording.
 *
 * One width for every timecode in every transcript is what makes them comparable at a glance and
 * greppable by anything reading the document back; a `12:03` that means twelve minutes in one
 * file and twelve hours in another is the ambiguity this avoids paying two characters for.
 */
export function formatTranscriptTimecode(seconds) {
    const total = Math.max(0, Math.floor(seconds));
    const hours = Math.floor(total / 3_600);
    const minutes = Math.floor((total % 3_600) / 60);
    return [hours, minutes, total % 60].map((part) => String(part).padStart(2, "0")).join(":");
}
/** `00:12:03–00:12:41`, or just the start when the end could not be read. */
export function formatTranscriptTurnTimecode(facts) {
    if (facts.startSeconds === undefined)
        return null;
    const start = formatTranscriptTimecode(facts.startSeconds);
    if (facts.endSeconds === undefined || facts.endSeconds <= facts.startSeconds)
        return start;
    return `${start}${TRANSCRIPT_TIMECODE_RANGE_SEPARATOR}${formatTranscriptTimecode(facts.endSeconds)}`;
}
/**
 * The whole header line, or `null` for a turn the vendor labelled neither speaker nor time on.
 *
 * `null` rather than an empty string, because a turn with no header is a block that is nothing but
 * its words — there is no blank first line to skip when it is read back.
 */
export function formatTranscriptTurnHeader(facts, names) {
    const parts = [
        facts.speaker === undefined ? null : transcriptSpeakerDisplayLabel(facts.speaker, names),
        formatTranscriptTurnTimecode(facts),
    ].filter((part) => part !== null);
    return parts.length === 0 ? null : parts.join(TRANSCRIPT_TURN_HEADER_SEPARATOR);
}
/** `Speaker 1`, and nothing that merely starts that way. */
const SPEAKER_LABEL_PATTERN = /^Speaker (\d+)$/;
/**
 * `HH:MM:SS`, with at least two hour digits so a padded zero is required rather than tolerated.
 *
 * Minutes and seconds are bounded at 59 because the writer never emits more, and accepting a
 * `00:99:99` here would let a line the format could not have produced read as a timecode.
 */
const TIMECODE_PATTERN = /^(\d{2,}):([0-5]\d):([0-5]\d)$/;
/**
 * One turn header's facts, or `null` when the line is not a header this module wrote.
 *
 * The caller is a reader deciding whether a block's first line is chrome or speech, so every
 * shape that is not exactly one of the three the writer emits — a speaker, a timecode, or a
 * speaker and a timecode — has to come back `null`.
 */
export function parseTranscriptTurnHeader(line) {
    const parts = line.trim().split(TRANSCRIPT_TURN_HEADER_SEPARATOR);
    if (parts.length > 2)
        return null;
    const [first, second] = parts;
    if (second !== undefined) {
        const speaker = parseSpeakerLabel(first);
        const timecode = parseTurnTimecode(second);
        return speaker === undefined || timecode === null ? null : { speaker, ...timecode };
    }
    const speaker = parseSpeakerLabel(first);
    if (speaker !== undefined)
        return { speaker };
    const timecode = parseTurnTimecode(first);
    return timecode === null ? null : timecode;
}
function parseSpeakerLabel(value) {
    const match = SPEAKER_LABEL_PATTERN.exec(value);
    if (!match)
        return undefined;
    const speaker = Number(match[1]);
    // The writer only ever labels a positive integer; `Speaker 0` is a line it could not have
    // produced, so it is not a header rather than a header naming an impossible speaker.
    return Number.isInteger(speaker) && speaker > 0 ? speaker : undefined;
}
/** A start, or a start and an end. `null` when the text is not a timecode at all. */
function parseTurnTimecode(value) {
    const ends = value.split(TRANSCRIPT_TIMECODE_RANGE_SEPARATOR);
    if (ends.length > 2)
        return null;
    const startSeconds = parseTranscriptTimecode(ends[0] ?? "");
    if (startSeconds === undefined)
        return null;
    if (ends.length === 1)
        return { startSeconds };
    const endSeconds = parseTranscriptTimecode(ends[1] ?? "");
    return endSeconds === undefined ? null : { startSeconds, endSeconds };
}
/**
 * One stored block's text with its speaker's given name in place of `Speaker N`.
 *
 * **Only the header line is touched**, parsed and rewritten by the same two bodies above. A bare
 * string replace over the whole text would rewrite a sentence that merely *mentions* "Speaker 1"
 * and hand back words nobody spoke. Everything else comes back byte-identical, a block whose first
 * line only looks like a header included.
 *
 * A projection, never a rewrite: the stored blocks keep saying `Speaker N`, so a name can be
 * changed or cleared with nothing re-ingested.
 */
export function applyTranscriptSpeakerNames(text, names) {
    if (names === undefined)
        return text;
    const breakAt = text.indexOf("\n");
    const firstLine = breakAt < 0 ? text : text.slice(0, breakAt);
    const header = parseTranscriptTurnHeader(firstLine);
    if (header?.speaker === undefined)
        return text;
    const rewritten = formatTranscriptTurnHeader(header, names);
    if (rewritten === null || rewritten === firstLine)
        return text;
    return breakAt < 0 ? rewritten : `${rewritten}${text.slice(breakAt)}`;
}
/** `01:02:05` as seconds, or nothing when the text is not one whole timecode. */
export function parseTranscriptTimecode(value) {
    const match = TIMECODE_PATTERN.exec(value);
    if (!match)
        return undefined;
    return Number(match[1]) * 3_600 + Number(match[2]) * 60 + Number(match[3]);
}
